import { api, ApiError } from '@/core/lib/apiClient';
import { LiveDocument, type Canonical } from './liveDocument';
import { targetLength, type Operation } from './textOperation';
interface Snapshot {
  body: string;
  version: number;
  resourceRevision: number;
  state: string;
  operations?: Canonical[];
  hasMore?: boolean;
}
export interface Presence {
  clientId: string;
  person: { name: string; handle: string };
  anchor?: number | null;
  head?: number | null;
  version?: number;
  seen: number;
}
export interface LiveState {
  body: string;
  version: number;
  revision: number;
  pending: boolean;
  ready: boolean;
  error: string;
  peers: Presence[];
}
/** The socket wakes a durable HTTP sync. Only one immutable operation is in flight. */
export class LiveClient {
  private doc?: LiveDocument;
  private stopped = false;
  private syncing = false;
  private blocked = false;
  private timer?: ReturnType<typeof setInterval>;
  private reconnect?: ReturnType<typeof setTimeout>;
  private socket?: WebSocket;
  private peers = new Map<string, Presence>();
  private clientId = '';
  private revision = 0;
  private state = 'draft';
  private error = '';
  private key: string;
  private path: string;
  private lastPresence = 0;
  private slug: string;
  private emit: (state: LiveState, remote?: Operation | null) => void;
  constructor(
    owner: string,
    slug: string,
    emit: (state: LiveState, remote?: Operation | null) => void,
  ) {
    this.slug = slug;
    this.emit = emit;
    this.key = `truxsocial:web-live:${owner}:${slug}`;
    this.path = `/post-collaborations/${encodeURIComponent(slug)}/live/`;
  }
  private save() {
    if (this.doc)
      try {
        if (this.doc.pending.length) localStorage.setItem(this.key, this.doc.serialize());
        else localStorage.removeItem(this.key);
      } catch {
        this.error =
          'Browser recovery is unavailable. Keep this editor open until all changes are saved.';
      }
  }
  private notify(remote?: Operation | null) {
    if (this.stopped) return;
    this.emit(
      {
        body: this.doc?.body ?? '',
        version: this.doc?.version ?? 0,
        revision: this.revision,
        pending: !!this.doc?.pending.length,
        ready: !!this.doc && !this.blocked && this.state === 'draft',
        error: this.error,
        peers: [...this.peers.values()].filter(
          (p) => p.clientId !== this.clientId && Date.now() - p.seen < 45000,
        ),
      },
      remote,
    );
  }
  async start() {
    try {
      const saved = localStorage.getItem(this.key);
      if (saved) this.doc = LiveDocument.restore(saved);
    } catch {
      this.blocked = true;
      this.error =
        'Saved edits could not be restored safely. Your recovery has been retained in this browser.';
      this.notify();
      return;
    }
    await this.sync();
    if (this.stopped) return;
    this.connect();
    this.timer = setInterval(() => {
      void this.sync();
      if (this.socket?.readyState === WebSocket.OPEN)
        this.socket.send(JSON.stringify({ type: 'ping' }));
    }, 10000);
  }
  private connect() {
    if (this.stopped || this.blocked) return;
    const ws = new WebSocket(
      `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/ws/drafts/${encodeURIComponent(this.slug)}/`,
    );
    this.socket = ws;
    ws.onmessage = (e) => {
      try {
        const m = JSON.parse(String(e.data));
        if (m.type === 'draft.ready') this.clientId = m.clientId;
        if (m.type === 'draft.presence') {
          if (m.online) this.peers.set(m.clientId, { ...m, seen: Date.now() });
          else this.peers.delete(m.clientId);
          this.notify();
        } else if (m.type !== 'draft.pong') void this.sync();
      } catch {
        /* Ignore malformed transport wakeups; HTTP remains authoritative. */
      }
    };
    ws.onclose = (e) => {
      if (this.stopped) return;
      if ([4401, 4403].includes(e.code)) {
        this.blocked = true;
        this.error = 'Live access ended. Your unsent changes remain in this browser.';
        this.notify();
      } else this.reconnect = setTimeout(() => this.connect(), 5000);
    };
    ws.onerror = () => {
      /* Polling continues while transport is unavailable. */
    };
  }
  edit(body: string) {
    if (!this.doc || this.stopped || this.blocked) return;
    try {
      this.doc.edit(body);
    } catch {
      this.error =
        'This edit is too large or contains invalid text. The previous draft is preserved.';
      this.notify();
      return;
    }
    this.save();
    this.notify();
    void this.sync();
  }
  presence(anchor: number, head: number) {
    if (
      !this.doc ||
      this.doc.pending.length ||
      Date.now() - this.lastPresence < 300 ||
      this.socket?.readyState !== WebSocket.OPEN
    )
      return;
    this.lastPresence = Date.now();
    this.socket.send(JSON.stringify({ type: 'presence', version: this.doc.version, anchor, head }));
  }
  async sync() {
    if (this.stopped || this.syncing || this.blocked) return;
    this.syncing = true;
    try {
      let more = true;
      while (more && !this.stopped) {
        const snap = await api<Snapshot>(
          this.path + (this.doc ? `?after=${this.doc.version}` : ''),
        );
        if (this.stopped || this.blocked) return;
        if (!this.doc) this.doc = new LiveDocument(snap.body, snap.version);
        else
          for (const op of snap.operations ?? []) {
            const remote = this.doc.receive(op);
            this.save();
            this.notify(remote);
          }
        this.revision = snap.resourceRevision;
        this.state = snap.state;
        more = !!snap.hasMore;
      }
      if (!this.doc || this.stopped || this.blocked) return;
      if (this.state !== 'draft') {
        this.blocked = true;
        this.error =
          'This draft has been attached to a post. Use the collaboration page to propose approved changes.';
        return;
      }
      if (this.doc.pending.length) {
        const head = this.doc.pending[0]!;
        if (!head.sent && targetLength(head.operation) > 5000) {
          this.error = 'Shorten the shared draft to 5,000 characters before it can sync.';
          return;
        }
        const request = this.doc.request()!;
        this.save();
        try {
          await api<Canonical>(this.path, { method: 'POST', body: request });
        } catch (e) {
          if (e instanceof ApiError && e.code === 'live_sync_required')
            this.doc.rebaseRejected(request.operationId);
          else if (e instanceof ApiError && e.code === 'live_text_limit')
            this.doc.resolveRejectedText(request.operationId);
          throw e;
        }
        // Read the canonical log before acknowledging: another writer may precede our edit.
      }
      this.error = '';
    } catch (e) {
      if (e instanceof ApiError && [401, 403, 404].includes(e.status)) {
        this.blocked = true;
        this.error = 'Live access ended. Unsent changes remain in this browser.';
      } else
        this.error =
          e instanceof ApiError
            ? 'Could not sync yet. Your edits are retained; retry after checking the connection.'
            : 'Connection or synchronization failed. Your edits are retained in this browser.';
    } finally {
      this.syncing = false;
      this.save();
      this.notify();
    }
    if (this.doc?.pending.length && !this.error && !this.stopped && !this.blocked)
      setTimeout(() => void this.sync(), 100);
  }
  stop() {
    this.stopped = true;
    this.save();
    clearInterval(this.timer);
    clearTimeout(this.reconnect);
    this.socket?.close();
  }
}
